import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Monitor } from 'lucide-react';

const RefundPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Refund & Cancellation Policy - URL Monitor Pro</title>
        <meta name="description" content="Read URL Monitor Pro's Refund and Cancellation Policy to understand our refund eligibility, cancellation process, and billing terms." />
        <meta name="keywords" content="refund policy, cancellation policy, billing terms, subscription cancellation" />
        <link rel="canonical" href="https://urlmonitor.pro/refund" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Monitor className="w-8 h-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">URL Monitor Pro</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <a href="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
              <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
              <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
              <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Start Free
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Refund & Cancellation Policy</h1>
          <p className="text-sm text-gray-500">Last updated: January 25, 2025</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-left">
            <style>
              {`
                .prose h2 {
                  margin-top: 2rem;
                  margin-bottom: 1rem;
                }
                .prose p {
                  margin-bottom: 1rem;
                  text-align: left;
                }
                .prose ul, .prose ol {
                  margin-top: 0.5rem;
                  margin-bottom: 1.5rem;
                  text-align: left;
                }
              `}
            </style>

            <h2>Company Information</h2>
            <p>
              This policy applies to all subscriptions purchased for URL Monitor Pro, a product operated by Publive Mediatech Private Limited.
            </p>
            <ul>
              <li>Company: Publive Mediatech Private Limited</li>
              <li>Website: <a href="https://www.thepublive.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.thepublive.com</a></li>
              <li>Location: Gurgaon, Haryana, India</li>
              <li>Product: URL Monitor Pro</li>
            </ul>

            <h2>1. Free Plan</h2>
            <p>
              Our Free plan is available at no cost and does not involve any payments. Therefore, no refunds are applicable for the Free plan.
            </p>

            <h2>2. Paid Plans (Pro, Business, Enterprise)</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Subscription Billing</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>All paid plans are billed on a monthly subscription basis</li>
                <li>Payments are processed automatically on your billing date</li>
                <li>Subscriptions auto-renew unless cancelled before the next billing cycle</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Refund Policy</h3>
              <p className="text-gray-700 mb-4">
                <strong>7-Day Money-Back Guarantee:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>New subscribers are eligible for a full refund within 7 days of their first payment</li>
                <li>Refund requests must be submitted within 7 days from the purchase date</li>
                <li>Refunds are processed within 7-10 business days</li>
                <li>This guarantee applies only to first-time purchases</li>
              </ul>

              <p className="text-gray-700 mb-4">
                <strong>After 7 Days:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>No refunds are provided for partial subscription periods</li>
                <li>You may cancel anytime, but no prorated refunds will be issued</li>
                <li>Service will continue until the end of your current billing cycle</li>
              </ul>

              <h2>3. Cancellation Policy</h2>
              
              <h3>3.1 How to Cancel</h3>
              <ul>
                <li>You can cancel your subscription anytime from your account Settings page</li>
                <li>Alternatively, contact us at support@urlmonitor.pro to request cancellation</li>
                <li>Cancellation is effective at the end of your current billing period</li>
              </ul>

              <h3>3.2 After Cancellation</h3>
              <ul>
                <li>You retain access to paid features until the end of your billing cycle</li>
                <li>Your account will automatically downgrade to the Free plan</li>
                <li>All monitor data is retained according to the Free plan's data retention policy (30 days)</li>
                <li>You can reactivate your subscription anytime</li>
              </ul>

              <h2>4. Downgrades</h2>
              <ul>
                <li>You can downgrade to a lower plan anytime</li>
                <li>Downgrades take effect at the end of your current billing cycle</li>
                <li>No partial refunds are issued for downgrades</li>
                <li>You must ensure your monitors fit within the new plan's limits before downgrading</li>
              </ul>

              <h2>5. Failed Payments</h2>
              <ul>
                <li>If a payment fails, we will retry billing up to 3 times over 7 days</li>
                <li>You will receive email notifications for failed payments</li>
                <li>If all retries fail, your account will be downgraded to the Free plan</li>
                <li>No refunds are issued for periods when service was unavailable due to failed payments</li>
              </ul>

              <h2>6. Service Interruptions</h2>
              <p>In case of extended service outages caused by us (not including scheduled maintenance):</p>
              <ul>
                <li>Outages lasting more than 24 continuous hours may qualify for a prorated credit</li>
                <li>Credits are issued at our sole discretion</li>
                <li>Credits are applied to your account, not refunded as cash</li>
                <li>Scheduled maintenance and third-party service issues are excluded</li>
              </ul>

              <h2>7. Refund Processing</h2>
              <p>When a refund is approved:</p>
              <ul>
                <li>Refunds are processed to the original payment method via Razorpay</li>
                <li>Processing time: 7-10 business days</li>
                <li>Refund timing depends on your bank or payment provider</li>
              </ul>

              <h2>8. Non-Refundable Items</h2>
              <p>The following are non-refundable:</p>
              <ul>
                <li>Subscription renewals (after the initial 7-day period)</li>
                <li>Partial subscription periods</li>
                <li>Services already rendered</li>
                <li>Account terminations due to Terms of Service violations</li>
              </ul>

              <h2>9. How to Request a Refund</h2>
              <p>To request a refund:</p>
              <ol>
                <li>Email us at <a href="mailto:support@urlmonitor.pro" className="text-blue-600 hover:text-blue-700 hover:underline">support@urlmonitor.pro</a></li>
                <li>Include your account email and order/transaction ID</li>
                <li>Specify the reason for your refund request</li>
                <li>We will review and respond within 2-3 business days</li>
              </ol>

              <h2>10. Changes to This Policy</h2>
              <p>
                We reserve the right to modify this Refund & Cancellation Policy at any time. Changes will be effective immediately upon posting. Your continued use of the service after changes constitutes acceptance of the updated policy.
              </p>

              <h2>11. Contact Us</h2>
              <p>For questions about refunds or cancellations, please contact us:</p>
              <div className="bg-gray-50 rounded-lg p-6 not-prose">
                <p className="text-gray-700 mb-2"><strong>Publive Mediatech Private Limited</strong></p>
                <p className="text-gray-700">Email: <a href="mailto:support@urlmonitor.pro" className="text-blue-600 hover:text-blue-700 hover:underline">support@urlmonitor.pro</a></p>
                <p className="text-gray-700">Website: <a href="https://www.thepublive.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">www.thepublive.com</a></p>
                <p className="text-gray-700">Location: Gurgaon, Haryana, India</p>
              </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-2 mb-4 md:mb-0">
              <Monitor className="w-8 h-8 text-blue-400" />
              <span className="text-xl font-bold">URL Monitor Pro</span>
            </Link>
            <p className="text-gray-400">
              © 2025 URL Monitor Pro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RefundPage;
